import { useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { useReveal } from '@/hooks/useReveal';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import ragImg from '@/assets/blog-rag.png';
import roiImg from '@/assets/blog-roi.png';
import llmImg from '@/assets/blog-llm.png';

const fallbackImages = [ragImg, roiImg, llmImg];

const BlogSection = () => {
  const { t, language } = useTranslation();
  useReveal();

  const { data: livePosts } = useQuery({
    queryKey: ['blog_latest_3'],
    queryFn: async () => {
      const { data } = await supabase.from('blog_posts').select('*').eq('status', 'published').order('published_at', { ascending: false }).limit(3);
      return data;
    },
  });

  const hasLivePosts = livePosts && livePosts.length > 0;

  // Re-observe dynamically rendered cards after data loads
  useEffect(() => {
    if (!hasLivePosts) return;
    const els = document.querySelectorAll('#blog .rev:not(.in), #blog .rev-l:not(.in), #blog .rev-r:not(.in)');
    if (!els.length) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('in');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '-40px' });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [hasLivePosts]);

  return (
    <section id="blog">
      <div className="blog-header rev">
        <div>
          <div className="section-tag">{t.blog.tag}</div>
          <h2 className="section-h" style={{ whiteSpace: 'pre-line' }}>{t.blog.title}</h2>
        </div>
        <Link to="/blog" className="blog-all">{t.blog.allArticles}</Link>
      </div>

      <div className="blog-grid">
        {hasLivePosts ? (
          livePosts.map((post, i) => {
            const title = language === 'ro' ? (post.title_ro || post.title_en) : post.title_en;
            const excerpt = language === 'ro' ? (post.excerpt_ro || post.excerpt_en) : post.excerpt_en;
            return (
              <Link to={`/blog/${post.slug}`} className={`blog-card${i === 0 ? ' featured' : ''} rev del-${i + 1}`} key={post.id}>
                <div className="blog-card-img">
                  <img src={post.cover_image || fallbackImages[i]} alt={title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <span className="blog-cat">{(post as any).category || 'news'}</span>
                </div>
                <div className="blog-card-body">
                  <div className="blog-meta">
                    <span>📅 {post.published_at ? new Date(post.published_at).toLocaleDateString('en', { month: 'short', year: 'numeric' }) : ''}</span>
                    <span>⏱ {post.reading_time_min || 3} min</span>
                  </div>
                  <h3 className="blog-h">{title}</h3>
                  <p>{excerpt}</p>
                  <span className="blog-read">{i === 0 ? t.blog.readMore : t.blog.readShort}</span>
                </div>
              </Link>
            );
          })
        ) : (
          t.blog.posts.map((post, i) => (
            <div className={`blog-card${post.featured ? ' featured' : ''} rev del-${i + 1}`} key={i}>
              <div className="blog-card-img">
                <img src={fallbackImages[i]} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <span className="blog-cat">{post.category}</span>
              </div>
              <div className="blog-card-body">
                <div className="blog-meta">
                  <span>📅 {post.date}</span>
                  <span>⏱ {post.readTime}</span>
                </div>
                <h3 className="blog-h">{post.title}</h3>
                <p>{post.excerpt}</p>
                <a href="#" className="blog-read">
                  {post.featured ? t.blog.readMore : t.blog.readShort}
                </a>
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
};

export default BlogSection;
